import React from 'react';
import { Box, Typography, Paper, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress, Alert } from '@mui/material';
import { CorporateActionTypeLabels, CorporateActionTypeColors } from '../../stockTradingEnums';

const formatPrice = (val) => val != null ? `$${Number(val).toFixed(2)}` : '-';
const formatPercent = (val) => val != null ? `${Number(val).toFixed(2)}%` : '-';
const formatDate = (val) => val ? new Date(val).toLocaleDateString() : '-';
const formatDateTime = (val) => val ? new Date(val).toLocaleString() : '-';
const formatNumber = (val) => val != null ? Number(val).toLocaleString() : '-';

const SectionTitle = ({ children }) => (
    <Typography variant="h6" sx={{ mb: 2, mt: 3, fontWeight: 600, fontSize: '1rem' }}>{children}</Typography>
);
const EmptyState = ({ message }) => (
    <Box sx={{ py: 4, textAlign: 'center' }}><Typography color="text.secondary">{message || 'No data available'}</Typography></Box>
);
const TabLoading = () => (
    <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress /></Box>
);
const SignalChip = ({ signal }) => {
    const colorMap = { BUY: 'success', SELL: 'error', NEUTRAL: 'default', LONG_VOL: 'success', SHORT_VOL: 'error' };
    return <Chip label={signal || '-'} size="small" color={colorMap[signal] || 'default'} />;
};

const FinancialTab = ({ data, loading, error }) => {
    if (loading) return <TabLoading />;
    if (error) return <Alert severity="error">{error}</Alert>;
    if (!data) return <TabLoading />;

    const priceHistory = data.price_history || [];
    const corporateActions = data.corporate_actions || [];
    const earnings = data.earnings || [];
    const realizedVol = data.realized_volatility || [];

    return (
        <Box>
            {/* Price History */}
            <SectionTitle>Price History (Daily)</SectionTitle>
            {priceHistory.length === 0 ? <EmptyState message="No price history available" /> : (
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Date</TableCell>
                                <TableCell align="right">Open</TableCell>
                                <TableCell align="right">High</TableCell>
                                <TableCell align="right">Low</TableCell>
                                <TableCell align="right">Close</TableCell>
                                <TableCell align="right">Volume</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {priceHistory.map((row, i) => (
                                <TableRow key={i}>
                                    <TableCell>{formatDate(row.date)}</TableCell>
                                    <TableCell align="right">{formatPrice(row.open)}</TableCell>
                                    <TableCell align="right">{formatPrice(row.high)}</TableCell>
                                    <TableCell align="right">{formatPrice(row.low)}</TableCell>
                                    <TableCell align="right">{formatPrice(row.close)}</TableCell>
                                    <TableCell align="right">{formatNumber(row.volume)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Corporate Actions */}
            <SectionTitle>Corporate Actions</SectionTitle>
            {corporateActions.length === 0 ? <EmptyState message="No corporate actions" /> : (
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Type</TableCell>
                                <TableCell>Ex-Date</TableCell>
                                <TableCell align="right">Amount</TableCell>
                                <TableCell>Description</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {corporateActions.map((row, i) => (
                                <TableRow key={i}>
                                    <TableCell>
                                        <Chip label={CorporateActionTypeLabels[row.action_type] || row.action_type} size="small" color={CorporateActionTypeColors[row.action_type] || 'default'} />
                                    </TableCell>
                                    <TableCell>{formatDate(row.ex_date)}</TableCell>
                                    <TableCell align="right">{row.amount != null ? `$${Number(row.amount).toFixed(4)}` : '-'}</TableCell>
                                    <TableCell>{row.description || '-'}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Earnings */}
            <SectionTitle>Earnings</SectionTitle>
            {earnings.length === 0 ? <EmptyState message="No earnings data" /> : (
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Date</TableCell>
                                <TableCell>Quarter</TableCell>
                                <TableCell align="right">Est. EPS</TableCell>
                                <TableCell align="right">Actual EPS</TableCell>
                                <TableCell align="right">Surprise %</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {earnings.map((row, i) => (
                                <TableRow key={i}>
                                    <TableCell>{formatDate(row.earnings_date)}</TableCell>
                                    <TableCell>{row.fiscal_quarter || '-'}</TableCell>
                                    <TableCell align="right">{row.estimated_eps != null ? `$${Number(row.estimated_eps).toFixed(2)}` : '-'}</TableCell>
                                    <TableCell align="right">{row.actual_eps != null ? `$${Number(row.actual_eps).toFixed(2)}` : '-'}</TableCell>
                                    <TableCell align="right">
                                        {row.surprise_percent != null ? (
                                            <Typography
                                                component="span"
                                                sx={{ color: row.surprise_percent >= 0 ? '#22c55e' : '#ef4444', fontWeight: 500 }}
                                            >
                                                {row.surprise_percent >= 0 ? '+' : ''}{Number(row.surprise_percent).toFixed(2)}%
                                            </Typography>
                                        ) : '-'}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Realized Volatility */}
            <SectionTitle>Realized Volatility</SectionTitle>
            {realizedVol.length === 0 ? <EmptyState message="No realized volatility data" /> : (
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Estimator</TableCell>
                                <TableCell align="right">Window (Days)</TableCell>
                                <TableCell align="right">Annualized Value</TableCell>
                                <TableCell>Calculation Date</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {realizedVol.map((row, i) => (
                                <TableRow key={i}>
                                    <TableCell>{row.estimator_type || '-'}</TableCell>
                                    <TableCell align="right">{row.window_days || '-'}</TableCell>
                                    <TableCell align="right">{formatPercent(row.annualized_value)}</TableCell>
                                    <TableCell>{formatDate(row.calculation_date)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
};

export default FinancialTab;
